import type { FC } from 'react';
import { useState } from 'react';

import { ReferralDisplayedStatus } from '__generated__/GQL';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import TrackView from 'features/Analytics/TrackView';
import { useReferral } from 'features/Referral/hooks/useReferral';
import EmptyReferralsList from 'features/Referral/pages/ReferralList/components/EmptyReferralsList';
import Filters, {
  FilterState,
} from 'features/Referral/pages/ReferralList/components/Filters';
import FinishedReferralsList from 'features/Referral/pages/ReferralList/components/FinishedReferralsList';
import PendingReferralsList from 'features/Referral/pages/ReferralList/components/PendingReferralsList';
import * as Layout from 'layouts/StepperLayout';

import locales from './locales';

const ReferralList: FC = () => {
  const PENDING_STATUSES = [
    ReferralDisplayedStatus.UserCreated,
    ReferralDisplayedStatus.NoTransaction,
    ReferralDisplayedStatus.AccountOpened,
  ];

  const FINISHED_STATUTES = [
    ReferralDisplayedStatus.ReferrerRewarded,
    ReferralDisplayedStatus.Sleeping,
  ];

  const isPending = (status: ReferralDisplayedStatus): boolean => {
    return PENDING_STATUSES.includes(status);
  };

  const isFinished = (status: ReferralDisplayedStatus): boolean => {
    return FINISHED_STATUTES.includes(status);
  };

  const [filters, setFilters] = useState<FilterState>(FilterState.None);

  const { error, loading, referralCodes, referrals } = useReferral(() => {
    if (
      referrals.length > 0 &&
      referrals.filter((r) => isPending(r.displayedStatus)).length > 0
    ) {
      setFilters(FilterState.Pending);
      return;
    }

    if (
      referrals.length > 0 &&
      referrals.filter((r) => isFinished(r.displayedStatus)).length > 0
    ) {
      setFilters(FilterState.Finished);
      return;
    }

    setFilters(FilterState.None);
  });

  const pendingReferrals = referrals.filter((r) =>
    isPending(r.displayedStatus),
  );

  const finishedReferrals = referrals.filter((r) =>
    isFinished(r.displayedStatus),
  );

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorView />;
  }

  return (
    <Layout.Root>
      <TrackView pageName="Referral List" />
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton />
        </Layout.HeaderButtonsContainer>
        <Heading marginBottom="space-32" marginTop="space-24" size="lg">
          {locales.title}
        </Heading>
        <Text colorScheme="gray" marginBottom="space-32" size="md">
          {locales.description}
        </Text>
        <Filters
          finishedDisabled={finishedReferrals.length === 0}
          onChange={(selected) => setFilters(selected)}
          pendingDisabled={pendingReferrals.length === 0}
          selected={filters}
        />
        {referrals.length === 0 ? (
          <EmptyReferralsList
            referralCode={referralCodes[0]?.referralCode ?? null}
          />
        ) : (
          <>
            {filters === FilterState.Pending ? (
              <PendingReferralsList referrals={pendingReferrals} />
            ) : null}
            {filters === FilterState.Finished ? (
              <FinishedReferralsList referrals={finishedReferrals} />
            ) : null}
          </>
        )}
      </Layout.Body>
    </Layout.Root>
  );
};

export default ReferralList;
